import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { Card, InfoTooltip } from 'components';
import React, { ReactNode, useState, forwardRef } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { useIsMobile } from 'hooks';
import ClickAwayListener from '@mui/material/ClickAwayListener';

type OverviewStatsCardProps = {
  title: string;
  tooltipText: ReactNode;
  extraChip?: ReactNode;
  value: string | number;
  initialFieldValue?: string | number;
  valuePercentage?: string;
  valuePercentageColor?: string;
  valuePercentageBackgroundColor?: string;
  valuePercentageBorderColor?: string;
  secondaryText: string;
  inputProps?: TextFieldProps;
  onSubmit?: (value: string) => void;
  isExtended?: boolean;
};

export const OverviewStatsCard = forwardRef<HTMLDivElement, OverviewStatsCardProps>(function OverviewStatsCard(
  {
    title,
    tooltipText,
    value,
    secondaryText,
    inputProps,
    valuePercentage,
    valuePercentageColor = 'success.dark',
    valuePercentageBackgroundColor = 'success.light',
    valuePercentageBorderColor = 'success.main',
    onSubmit,
    isExtended,
    initialFieldValue,
    extraChip,
  }: OverviewStatsCardProps,
  ref
) {
  const [fieldValue, setFieldValue] = useState<string>(String(initialFieldValue) ?? '');
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const { isMobile } = useIsMobile();
  const fixedWidth = isExtended ? '594px' : '287px';

  return (
    <Card
      sx={{
        height: 'inherit',
        width: isMobile ? '100%' : fixedWidth,
      }}
    >
      <ClickAwayListener
        onClickAway={() => {
          setIsEditable(false);
          setFieldValue('');
        }}
      >
        <div ref={ref}>
          <Stack direction="row" alignItems="center">
            <Typography variant="h4" mb={2}>
              {title}
            </Typography>
            <Box sx={{ display: 'inline-block', transform: 'translateY(-4px)', marginLeft: '8px' }}>
              <InfoTooltip placement="top" text={tooltipText} />
            </Box>
          </Stack>
          <Stack mb={3} direction="row" alignItems="center">
            <Typography variant="h1">{value}</Typography>
            {extraChip ? extraChip : null}
            {valuePercentage ? (
              <Chip
                label={valuePercentage}
                sx={(theme) => ({
                  width: 'fit-content',
                  backgroundColor: valuePercentageBackgroundColor,
                  color: valuePercentageColor,
                  border: '1px solid',
                  borderColor: valuePercentageBorderColor,
                  height: theme.spacing(6),
                  fontWeight: 'bold',
                  marginLeft: theme.spacing(1),
                })}
              />
            ) : null}
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="start">
            {isEditable ? (
              <Stack width={isMobile ? '100%' : 'inherit'}>
                <TextField
                  {...inputProps}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={fieldValue}
                  onChange={(e) => setFieldValue(e.target.value)}
                />
                <Stack direction="row" spacing={1} mt={2} justifyContent="space-between">
                  <Button
                    size="small"
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setIsEditable(false);
                      setFieldValue('');
                    }}
                    sx={{ marginLeft: '8px', height: 'auto' }}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    onClick={() => {
                      onSubmit?.(fieldValue);

                      setIsEditable(false);
                      setFieldValue('');
                    }}
                    sx={{ marginTop: '8px', height: 'auto' }}
                  >
                    Speichern
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <>
                <Typography variant="body2">{secondaryText}</Typography>
                {inputProps && !inputProps.disabled && !isEditable ? (
                  <Button
                    onClick={() => {
                      setIsEditable(true);
                    }}
                    sx={{ marginLeft: '8px', height: 'auto' }}
                    startIcon={<EditOutlined />}
                  >
                    Anpassen
                  </Button>
                ) : null}
              </>
            )}
          </Stack>
        </div>
      </ClickAwayListener>
    </Card>
  );
});
